import React from "react";

import { getConditionLabel } from "@features/ax/utils";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardHeader, CardTitle } from "@shared/ui/card";
import { AlertTriangle, Calendar, DollarSign, Wrench } from "lucide-react";
import { useParams } from "react-router-dom";
import { usePerformanceDetail } from "../../hooks";

interface AssetKeyMetricsCardsProps {
  data: any;
}

export const AssetKeyMetricsCards: React.FC<AssetKeyMetricsCardsProps> = ({
  data,
}) => {
  const { id } = useParams();
  const currency = useCurrency();
  const { label, color } = getConditionLabel(data?.conditionScores);
  const { data: performanceData } = usePerformanceDetail(id);
  // KPI data adapted for QuickStatsCard interface
  const kpiData = [
    {
      title: "Current Value",
      count: data?.currentValue,
      displayValue: `${currency} ${data?.currentValue ?? "NA"}`,
      trendSummary: ``,
      icon: <DollarSign className="h-6 w-6 text-blue-600" />,
      tooltip:
        "The current value of the asset on the balance sheet, shown as a percentage of its total replacement cost.",
    },
    {
      title: "Asset Age",
      count: data?.currentAge,
      displayValue: `${data?.currentAge ?? "NA"} Years`,
      trendSummary: "Years in service",
      icon: <Calendar className="h-6 w-6 text-purple-600" />,
      tooltip:
        "The total time this asset has been in service since its installation date.",
    },
    {
      title: "Condition Score",
      count: data?.conditionScore,
      displayValue: `${data?.conditionScores || 0}/100`,
      trendSummary: label,
      icon: <AlertTriangle className="h-6 w-6 text-green-600" />,
      tooltip:
        "The overall physical health of the asset, based on the most recent condition assessment.",
    },
    {
      title: "Maintenance (12mo)",
      count:
        performanceData?.overview?.last_12MonthMaintainceData?.value ?? "NA",
      trendSummary: "Activities completed",
      icon: <Wrench className="h-6 w-6 text-orange-600" />,
      tooltip:
        "The total number of maintenance work orders (planned and unplanned) completed for this asset in the last 12 months.",
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="text-xs">📊</div>
          <CardTitle className="text-lg font-semibold">Key Metrics</CardTitle>
        </div>
      </CardHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, index) => (
          <QuickStatsCard
            key={index}
            title={kpi.title}
            count={kpi.count}
            displayValue={kpi.displayValue}
            trendSummary={kpi.trendSummary}
            icon={kpi.icon}
            tooltip={kpi.tooltip}
          />
        ))}
      </div>
    </Card>
  );
};
